import { useLocation, useSearchParams } from 'react-router-dom';

import { logBookPath, type LogBookRoute } from './logbookRoutes';

/** What a book's page shows: one day's entry, or every entry written so far. */
export type LogView = 'form' | 'history';

/**
 * In the address rather than in component state, so a link can open a book
 * straight onto its history. No param is the day's page, which keeps every
 * existing link to a book meaning what it always meant.
 */
export const LOG_VIEW_PARAM = 'view';

/** Router state the hub's Logs card sends along — see `useLogView`. */
const FROM_LOGS_CARD = 'logs-card';

/** A book's history, opened from the hub's Logs card. Spread onto a `Link`. */
export const logBookHistoryLink = (book: LogBookRoute) => ({
  to: `${logBookPath(book)}?${LOG_VIEW_PARAM}=history`,
  state: { from: FROM_LOGS_CARD },
});

/**
 * Which view a book's page is on, and whether the reader arrived there from the
 * hub's Logs card.
 *
 * The card's rows open a book to read it, and its "New log entry" button is no
 * longer on screen, so a history reached that way offers the new entry itself.
 * A history reached by the page's own History button does not. The origin rides
 * in router state rather than the URL, so it is not bookmarked or shared along
 * with the address.
 */
export const useLogView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  const view: LogView = searchParams.get(LOG_VIEW_PARAM) === 'history' ? 'history' : 'form';

  const openedFromLogsCard =
    view === 'history' && (state as { from?: string } | null)?.from === FROM_LOGS_CARD;

  /**
   * Replaces rather than pushes, as the component state it took over from did:
   * it swaps what one page shows, so browser Back still leaves the book. It sends
   * no router state, so this history carries no new-entry shortcut.
   */
  const openHistory = () =>
    setSearchParams(
      (prev) => {
        prev.set(LOG_VIEW_PARAM, 'history');
        return prev;
      },
      { replace: true },
    );

  /**
   * Today's page, with no date and no view — the same place the card's "New log
   * entry" picker lands. Pushed, so Back returns to the history.
   */
  const startNewEntry = () => setSearchParams(new URLSearchParams());

  return { view, openHistory, openedFromLogsCard, startNewEntry };
};
